"use client";

/**
 * TrackingProvider - mounts the pageview + heartbeat loop.
 *
 * Next.js App Router re-runs ``usePathname()`` on every client-side
 * navigation, so watching that hook gives us one trigger per
 * distinct page the user visits. On change we:
 *
 *   1. Strip the locale prefix so server-side rows are locale-
 *      agnostic (locale is a separate column).
 *   2. Call ``trackPageview`` which handles session continuity +
 *      first-in-session detection + campaign params + referrer.
 *   3. Register a 15 s heartbeat + visibility listener + pagehide
 *      final-flush for the new path.
 *
 * Renders nothing - purely side-effectful. Mounted inside
 * ``AuthProvider`` in the root layout so the session cookie is
 * already readable when the first pageview fires.
 */

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import {
  currentTrackSessionId,
  setVisibilityActive,
  trackEvent,
  trackHeartbeat,
  trackPageEnd,
  trackPageview,
} from "@/lib/tracking";
import { notePageView } from "@/lib/interruption-budget";
import { useLocale } from "@/lib/locale-context";

const HEARTBEAT_INTERVAL_MS = 15 * 1000;

function stripLocale(path: string): { path: string; locale: string | null } {
  const m = /^\/(az|ru|en)(\/|$)/.exec(path);
  if (!m) return { path, locale: null };
  // Keep the slash: /az/foo  -> /foo  ; /az/ -> /  ; /az  -> /
  const rest = path.slice(m[1].length + 1) || "/";
  return { path: rest.startsWith("/") ? rest : `/${rest}`, locale: m[1] };
}

export default function TrackingProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { locale } = useLocale();
  // Retains the previous same-origin path so SPA navigations can
  // report it as ``referrer_path`` without re-parsing history.
  const lastPathRef = useRef<string | null>(null);

  // Whitelisted query param baked into the tracked path: /map serves
  // four distinct products behind ``?layer=`` (listings / hexes /
  // districts / zones), and a layer switch is a real navigation even
  // though the pathname doesn't change. Whitelist-only - arbitrary
  // filter params must NOT leak into page_views rows (cardinality +
  // privacy).
  const mapLayer =
    pathname && stripLocale(pathname).path === "/map"
      ? searchParams.get("layer")
      : null;

  useEffect(() => {
    if (!pathname) return;
    const { path, locale: pathLocale } = stripLocale(pathname);
    // Prefer the locale context (reactive to the toggle) over the
    // one derived from the URL path. They agree on cold-start; the
    // context wins once the toggle flips locale without navigating.
    const usedLocale = locale || pathLocale;
    const prev = lastPathRef.current;
    // ``?layer=`` rides along on /map so the admin pages report
    // splits the map by layer. Absent param = the default listings
    // layer, tracked as bare /map.
    // A shared list's id in the Telegram Mini App is its invitation:
    // page_views keep the screen, never the list.
    const tracked = path === "/map" && mapLayer
      ? `/map?layer=${mapLayer}`
      : path.replace(/^\/tg\/s\/[^/]+/, "/tg/s/[id]");
    trackPageview(tracked, usedLocale, prev);
    // The interruption budget counts page views in the same session
    // (lib/interruption-budget): nothing comes up on its first one. A
    // re-run on the same path is no new page: a language switch, or
    // Strict Mode's second run in development (it made the first page
    // read as the second).
    if (prev !== tracked) notePageView(currentTrackSessionId());
    lastPathRef.current = tracked;
  }, [pathname, locale, mapLayer]);

  // PWA session-start ping. Fires once per browser session when the
  // app is running as an installed PWA (`display-mode: standalone`
  // OR iOS `navigator.standalone`). Idempotent via sessionStorage so
  // SPA navigations within the same session don't re-fire. The event
  // gives us an authoritative "PWA opens" counter on the admin
  // activity feed without having to derive it from min(ts) per
  // session over the page_views table.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const FIRED_KEY = "menzil-pwa-session-fired";
      if (sessionStorage.getItem(FIRED_KEY)) return;
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      if (!isStandalone) return;
      const minimalUi = window.matchMedia?.("(display-mode: minimal-ui)").matches || false;
      trackEvent("pwa.session_start", {
        display_mode: minimalUi ? "minimal-ui" : "standalone",
        ios_legacy: !!(window.navigator as Navigator & { standalone?: boolean }).standalone,
      });
      sessionStorage.setItem(FIRED_KEY, String(Date.now()));
    } catch {
      /* sessionStorage unavailable in private mode etc. - skip */
    }
  }, []);

  // Heartbeat loop + visibility + pagehide. Lives in its own
  // effect so the interval isn't recreated on every navigation
  // (``pathname`` churn would otherwise reset the timer).
  useEffect(() => {
    if (typeof window === "undefined") return;

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        trackHeartbeat();
      }
    }, HEARTBEAT_INTERVAL_MS);

    const onVisibility = () => {
      setVisibilityActive(document.visibilityState === "visible");
    };

    // ``pagehide`` fires on back/forward-cache transitions too, which
    // is exactly the moment we want a final flush. Listener attached
    // to ``window`` rather than ``document`` so iOS fires it
    // reliably.
    const onPageHide = () => {
      trackPageEnd();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    // Outbound clicks - the single strongest intent moment (the user
    // is leaving to contact the seller on the source site). One
    // delegated listener covers every external anchor in the app;
    // capture-phase so target=_blank navigations can't outrun it.
    // Only host + a trimmed path land in props: enough to attribute
    // ("left to bina.az/items/12345 from /listings"), no query junk.
    const onClickCapture = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!/^https?:\/\//i.test(href)) return;
      try {
        const u = new URL(href);
        if (u.hostname === window.location.hostname) return;
        trackEvent("outbound.click", {
          host: u.hostname.replace(/^www\./, "").slice(0, 60),
          p: u.pathname.slice(0, 120),
        });
      } catch {
        /* malformed href - ignore */
      }
    };
    document.addEventListener("click", onClickCapture, true);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, []);

  return null;
}
